import React, { useMemo } from 'react'
import Icon from 'components/Icon'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { defaultImage } from './defaultImage'
import backgroundImage from './backgroundImage.jpg'
import { Button, Avatar } from '@arnsbogroup/react-voicearchive'
import { track, trackingProps } from 'tracking'
import { Card } from '@arnsbogroup/react-voicearchive'
import classNames from 'classnames'

export const ContactBanner = ({ sub, children, ...props }) => {
  const { formatMessage } = useIntl()

  return (
    <div
      className="w-full gap-2 grid grid-cols-1 md:grid-cols-3 bg-gray-100 bg-opacity-25 border border-gray-400 md:gap-8 my-8"
      {...props}
    >
      <div
        className="bg-cover hidden md:block"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="p-4">
        <h1 className="text-2xl">
          <FormattedMessage {...messages.title} />
        </h1>

        <div className="text-sm text-gray-700 mt-1">
          <FormattedMessage
            {...messages.description}
            values={{
              free: (
                <b className="font-medium text-tertiary">
                  {formatMessage(messages.free)}
                </b>
              ),
              name: (
                <b className="font-medium">{formatMessage(messages.name)}</b>
              ),
              email: (
                <a
                  href={`mailto:${formatMessage(messages.email)}`}
                  className="text-primary font-bold"
                  {...trackingProps(
                    'ContactInfo.Banner.description.email.click',
                    { email: formatMessage(messages.email) }
                  )}
                >
                  <FormattedMessage {...messages.write} />
                </a>
              ),
            }}
          />
        </div>
      </div>

      <div className="flex items-center justify-center p-4">
        <div className="text-center">
          <a href={`mailto:${formatMessage(messages.email)}`}>
            <Button
              className="px-10 block"
              {...trackingProps('ContactInfo.Banner.button.click', {
                email: formatMessage(messages.email),
              })}
            >
              <FormattedMessage
                {...messages.contact}
                values={{
                  name: formatMessage(messages.shortName),
                }}
              />
            </Button>
          </a>

          <a
            href={`tel:${formatMessage(messages.phone)}`}
            className="block text-xs font-medium uppercase mt-2"
            {...trackingProps('ContactInfo.Banner.phone.click', {
              tel: formatMessage(messages.phone),
            })}
          >
            <FormattedMessage
              {...messages.call}
              values={{
                phone: formatMessage(messages.phone),
              }}
            />
          </a>
        </div>
      </div>
    </div>
  )
}

export const ContactWidget = ({ sub, children, ...props }) => {
  const { formatMessage } = useIntl()

  const imageSrc = useMemo(() => {
    try {
      return new URL(formatMessage(messages.imageURL)).toString()
    } catch (_invalidUrlException) {
      return defaultImage
    }
  })

  return (
    <div {...props}>
      <div className="p-4 border border-gray-300">
        <div className="text-lg">
          <FormattedMessage {...messages.title} />
          <span className="text-tertiary ml-1">
            <FormattedMessage {...messages.free} />.
          </span>
        </div>

        <div className="flex justify-left md:justify-between items-center mt-4">
          <div className="w-24">
            <img alt={formatMessage(messages.title)} src={imageSrc} />
          </div>

          <div className="p-2 text-sm truncate">
            <div>
              <FormattedMessage {...messages.name} />
            </div>
            <a
              className="block"
              href={`tel:${formatMessage(messages.phone)}`}
              {...trackingProps('contact-info.widget.phone.click', {
                tel: formatMessage(messages.phone),
              })}
            >
              <FormattedMessage {...messages.phone} />
            </a>
            <a
              className="truncate text-primary"
              href={`mailto:${formatMessage(messages.email)}`}
              {...trackingProps('ContactInfo.Widget.email.click', {
                email: formatMessage(messages.email),
              })}
            >
              <FormattedMessage {...messages.email} />
            </a>
          </div>
        </div>

        <a
          href={`mailto:${formatMessage(messages.email)}`}
          {...trackingProps('ContactInfo.Widget.button.click', {
            email: formatMessage(messages.email),
          })}
        >
          <Button state="tertiary" className="mt-8 w-full">
            <FormattedMessage
              {...messages.contact}
              values={{
                name: formatMessage(messages.shortName),
              }}
            />
          </Button>
        </a>
      </div>
    </div>
  )
}

export const ContactHelpCard = ({ children, ...props }) => {
  const { formatMessage } = useIntl()

  return (
    <Card
      {...props}
      className='break-inside-avoid bg-gray-100 flex'
    >
      <div
        style={{ backgroundImage: `url(${backgroundImage})` }}
        className='bg-cover bg-center hidden flex-shrink-0 h-full w-1/3 xl:block'
      />

      <div className="p-4">
        <h1 className='text-base'>
          <FormattedMessage {...messages.titleContactHelpCard} />
        </h1>

        <div className="text-sm text-gray-700 mt-1">
          <FormattedMessage
            {...messages.descriptionContactHelpCard}
          />
        </div>
        <a
          className="text-tertiary text-sm font-medium hover:text-tertiary-darker"
          href='https://b24-tlgdhc.bitrix24.site/crm_form_rtul0'
          target='_blank'
          rel='noopener'
        >
          <FormattedMessage
            {...messages.contactContactHelpCard}
            values={{
              name: formatMessage(messages.shortNameContactHelpCard),
            }}
          />
        </a>
      </div>
    </Card>
  )
}

export const ContactCard = ({ children, compact = false, ...props }) => {
  const { formatMessage } = useIntl()

  return (
    <Card
      {...props}
      className={classNames('break-inside-avoid bg-gray-100', {
        flex: compact,
      })}
    >
      <div
        style={{ backgroundImage: `url(${backgroundImage})` }}
        className={classNames('bg-cover hidden flex-shrink-0', {
          'w-full h-40 md:block': !compact,
          'h-full w-1/3 xl:block': compact,
        })}
      />

      <div className="p-4">
        <h1 className={compact ? 'text-base' : 'text-2xl'}>
          <FormattedMessage {...messages.title} />
        </h1>

        <div className="text-sm text-gray-700 mt-1">
          <FormattedMessage
            {...messages.description}
            values={{
              free: (
                <b className="font-medium text-tertiary">
                  {formatMessage(messages.free)}
                </b>
              ),
              name: (
                <b className="font-medium">{formatMessage(messages.name)}</b>
              ),
              email: (
                <a
                  href={`mailto:${formatMessage(messages.email)}`}
                  className="text-primary font-bold"
                  {...trackingProps(
                    'ContactInfo.Banner.description.email.click',
                    { email: formatMessage(messages.email) }
                  )}
                >
                  <FormattedMessage {...messages.write} />
                </a>
              ),
            }}
          />
        </div>
        {compact && (
          <a
            className="text-tertiary text-sm font-medium hover:text-tertiary-darker"
            href={`mailto:${formatMessage(messages.email)}`}
          >
            <FormattedMessage
              {...messages.contact}
              values={{
                name: formatMessage(messages.shortName),
              }}
            />
          </a>
        )}
      </div>

      {!compact && (
        <div className="flex items-center justify-start p-4 space-x-4">
          <Avatar
            size="lg"
            src={new URL(formatMessage(messages.avatarUrl)).toString()}
          />
          <a
            href={`tel:${formatMessage(messages.phone)}`}
            className="block text-xs font-medium uppercase"
            {...trackingProps('ContactInfo.Banner.phone.click', {
              tel: formatMessage(messages.phone),
            })}
          >
            <Button
              state="tertiary"
              className="px-10 block"
            >
              <FormattedMessage
                {...messages.call}
                values={{
                  phone: formatMessage(messages.phone),
                  name: formatMessage(messages.shortName)
                }}
              />
            </Button>
          </a>
        </div>
      )}
    </Card>
  )
}
